import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getNovelById, getChaptersByNovel, deleteChapter } from '../../services/api';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';

const ChapterManagement = () => {
  const { novelId } = useParams();
  const [novel, setNovel] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [novelId]);

  const fetchData = async () => {
    try {
      const [novelData, chaptersData] = await Promise.all([
        getNovelById(novelId),
        getChaptersByNovel(novelId)
      ]);
      setNovel(novelData);
      setChapters(chaptersData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (chapterId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      await deleteChapter(chapterId);
      toast({
        title: "Chapter deleted",
        description: "The chapter has been removed."
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete chapter.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Link to="/admin/novels" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={16} />Back to Novels
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-serif text-gray-900">{novel?.title}</h1>
              <p className="text-sm text-gray-600 mt-1">Manage Chapters</p>
            </div>
            <Link to={`/admin/chapter/new/${novelId}`}>
              <Button className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800">
                <Plus size={16} />Add Chapter
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {chapters.length === 0 ? (
          <div className="bg-white border border-gray-200 p-12 text-center">
            <p className="text-gray-600 mb-4">No chapters yet. Add your first chapter!</p>
            <Link to={`/admin/chapter/new/${novelId}`}>
              <Button className="bg-gray-900 text-white hover:bg-gray-800">
                <Plus size={16} className="mr-2" />Add Chapter
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {chapters.map((chapter) => (
              <div key={chapter.id} className="bg-white border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm uppercase tracking-wider text-gray-500">Chapter {chapter.chapterNumber}</span>
                      <h3 className="text-xl font-serif text-gray-900">{chapter.title}</h3>
                    </div>
                    <p className="text-sm text-gray-500">
                      Published: {new Date(chapter.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-6">
                    <Link to={`/admin/chapter/edit/${chapter.id}`}>
                      <Button variant="outline" size="sm" className="flex items-center gap-2">
                        <Edit size={14} />Edit
                      </Button>
                    </Link>
                    <Button onClick={() => handleDelete(chapter.id, chapter.title)} variant="outline" size="sm" className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:border-red-600">
                      <Trash2 size={14} />Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChapterManagement;